import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { entities } from "../../../../constants/entities";
import useBaseService from "../../../../hooks/services/useBaseService";

/**
 * Monta o nome de exibição de um item de volume.
 * @param {Object} item O item de volume.
 * @returns {string} O nome do equipamento ou do produto formatado.
 */
const getItemDisplayName = (item) => {
  if (item.equipment) {
    return `${item.equipment.number} - ${item.equipment.name}`;
  }

  const productItem = item.product_item;
  if (productItem) {
    return [
      productItem.product?.name,
      productItem.category?.name,
      productItem.condition?.name,
    ]
      .filter(Boolean)
      .join(" | ");
  }

  return "-";
};

/**
 * Exibe os itens de um volume e a quantidade total.
 * Os itens só são buscados quando `open` é verdadeiro, para não sobrecarregar
 * a listagem com uma requisição por volume.
 */
const VolumeItemsDetails = ({ movementId, volumeId, open }) => {
  const { get: baseServiceGet } = useBaseService();

  const {
    data: items = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["volumeItems", movementId, volumeId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.movements.volumeItems(movementId, volumeId),
        { filters: {}, page: 1, view: "display" },
      );

      return response.result?.data ?? [];
    },
    enabled: open && !!movementId && !!volumeId,
    staleTime: 5 * 60 * 1000,
  });

  const totalQuantity = items.reduce(
    (acc, item) => acc + (Number(item.quantity) || 0),
    0,
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        Erro ao buscar os itens desse volume.
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        Esse volume não possui itens.
      </Alert>
    );
  }

  return (
    <Box sx={{ margin: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Item</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">
              Quantidade
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{getItemDisplayName(item)}</TableCell>
              <TableCell align="right">{item.quantity ?? "-"}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Quantidade total</TableCell>
            <TableCell align="right" sx={{ fontWeight: "bold" }}>
              {totalQuantity}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary">
        {items.length} {items.length === 1 ? "item" : "itens"} nesse volume
      </Typography>
    </Box>
  );
};

export default VolumeItemsDetails;
